/**
 * Verification Summary card (design §6 / fullscreen-spec §6), kind-variant by
 * LAYOUT only: pre-screening renders EXACTLY the 3 left-aligned ID / Income /
 * Rent tiles; full screening renders FIVE equal-width tiles filling the row —
 * the shared trio plus Court Records (openroom DTO, data-map §4 status chain)
 * and Credit Report. The shared-trio top bars are STATUS-TONED via the single
 * `verificationBarTone` rule, IDENTICAL for both kinds and matching the
 * combined report + PDF (green = verified, amber = pending, red = "Not
 * Completed"/failed, neutral = unavailable) — user 2026-06-16, overriding the
 * spec's pre-screen "green-only" bars so one status never shows two colours.
 * The TrueTenant-score block moved to the header card (spec §3); the credit
 * tile's "View" opens the CreditPreviewDialog (PDF preview + a Download button
 * inside), mirroring the other tiles and the combined report.
 *
 * INVARIANT: every credit/court-records-derived element gates on
 * screeningKind === 'full', NEVER on data presence — pre-pause pre-screen
 * pipelines used to enqueue OpenRoom too, so stale court data CAN exist for
 * old pre-screen apps; the kind gate, not presence, keeps those tiles out
 * (data-map §0). Since the OpenRoom resume (2026-07-02) pre-screenings no
 * longer enqueue or pull OpenRoom at all (queue-pipeline + worker gates).
 *
 * Student Bank=No (`idOnly`): same ID tile code as a normal report; other
 * tiles are simply not rendered.
 */
import {
	Banknote,
	CircleDollarSign,
	ClipboardPlus,
	FileText,
	ShieldCheck,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import {
	courtRecordRefs,
	courtRecordsTileView,
	deriveVerificationTiles,
	verificationBarTone,
} from '#app/utils/application-detail-v2/display'
import type {
	OpenroomSummary,
	ScreeningKind,
} from '#app/utils/application-detail-v2/types'
import { cn } from '#app/utils/misc'
import { CourtRecordsDialog } from './dialogs/CourtRecordsDialog'
import {
	DETAIL_CARD,
	IconSquare,
	type PastelTone,
	SectionIcon,
} from './primitives'

/** Status-toned top bar (spec §6 tone map, PastelTone saturated mates);
 * 'neutral' keeps the card's own light border — unavailable/paused. */
export type BarTone = 'green' | 'amber' | 'red' | 'neutral'

const BAR_TONES: Record<BarTone, string> = {
	green: 'border-t-green-600',
	// #F59F0A — the shared verification-bar amber, identical to the PDF's
	// `TONE.damber.fg` so the on-screen tile and the export can't drift.
	amber: 'border-t-[#F59F0A]',
	red: 'border-t-red-500',
	neutral: '',
}

const ACTION_CLASS =
	'cursor-pointer text-sm text-[#747474] transition-colors hover:text-[#32302F]'

export function VerificationTile({
	tone,
	icon,
	title,
	barTone,
	className,
	children,
}: {
	tone: PastelTone
	icon: ReactNode
	title: string
	barTone: BarTone
	className?: string
	/** The action row: a View button/link or muted status text. */
	children: ReactNode
}) {
	return (
		<div
			className={cn(
				'relative flex flex-col items-center gap-2 rounded-2xl border border-t-[5px] border-[#F0F0F0] bg-[#FBFDFD] px-6 pt-6 pb-4 text-center',
				BAR_TONES[barTone],
				className,
			)}
		>
			<IconSquare tone={tone}>{icon}</IconSquare>
			<span className="text-[17px] font-semibold text-[#32302F]">{title}</span>
			{children}
		</div>
	)
}

function ViewButton({
	onView,
	label = 'View',
}: {
	onView?: () => void
	/** 'View' opens a modal/dialog (every tile, incl. credit, now previews in a
	 * dialog rather than force-downloading). */
	label?: string
}) {
	return (
		<button type="button" onClick={onView} className={ACTION_CLASS}>
			{label}
		</button>
	)
}

export function MutedText({ children }: { children: ReactNode }) {
	return <span className="text-sm text-[#747474]">{children}</span>
}

export function VerificationSummary({
	screeningKind,
	aiReportData,
	hasVeriffDocuments,
	creditAvailable,
	openroom,
	openroomEnabled,
	idOnly = false,
	onViewId,
	onViewIncome,
	onViewRent,
	onViewCredit,
}: {
	screeningKind: ScreeningKind
	aiReportData: Record<string, unknown> | null
	hasVeriffDocuments: boolean
	/** downloads.creditReport — false by construction for pre-screening. */
	creditAvailable: boolean
	/** Court-records DTO — null by construction for pre-screening. */
	openroom: OpenroomSummary | null
	/** isOpenroomEnabled() — the Court Records tile only shows when OpenRoom is
	 * enabled platform-wide (OPENROOM_ENABLED); the rest of the summary always
	 * renders. */
	openroomEnabled: boolean
	/** Student Bank=No: omit Income/Rent/Credit/Court — ID tile unchanged. */
	idOnly?: boolean
	onViewId?: () => void
	onViewIncome?: () => void
	onViewRent?: () => void
	onViewCredit?: () => void
}) {
	const [courtDialogOpen, setCourtDialogOpen] = useState(false)
	const tiles = deriveVerificationTiles(aiReportData)
	const isFull = screeningKind === 'full'
	// COLOUR is status-toned and IDENTICAL for pre-screen and full (and the
	// combined report + PDF) — completed → green, "Not Completed"/failed → red,
	// pending → amber (user 2026-06-16, overriding spec §6's pre-screen
	// green-only bars). Only the LAYOUT stays kind-specific below.
	const tileWidth = isFull ? undefined : ''
	// Shared with the combined household tile (display.ts, 2026-09-08).
	const court = courtRecordsTileView(openroom)
	const courtRecords = courtRecordRefs(openroom)
	// With OpenRoom enabled a full screening renders FIVE tiles — widen the xl
	// grid so Court Records doesn't wrap to a second row (PDF keeps all tiles
	// on one row; the page must match).
	const showCourtTile = !idOnly && isFull && openroomEnabled

	return (
		<section className={cn(DETAIL_CARD, 'p-6 sm:p-7')}>
			<div className="flex items-center gap-3">
				<SectionIcon src="/img/verification-summary-icon.svg" />
				<h2 className="text-[19px] font-semibold text-[#32302F]">
					Verification Summary
				</h2>
			</div>
			<div
				className={cn(
					'mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2',
					idOnly
						? 'xl:grid-cols-4'
						: showCourtTile
							? 'xl:grid-cols-5'
							: 'xl:grid-cols-4',
				)}
			>
				<VerificationTile
					tone="green"
					icon={<ShieldCheck />}
					title="ID Verification"
					barTone={verificationBarTone(tiles.id.status)}
					className={tileWidth}
				>
					{tiles.id.present || hasVeriffDocuments ? (
						<ViewButton onView={onViewId} />
					) : (
						<MutedText>Not Available</MutedText>
					)}
				</VerificationTile>
				{!idOnly ? (
					<>
						<VerificationTile
							tone="rose"
							icon={<Banknote />}
							title="Income Verification"
							barTone={verificationBarTone(tiles.income.status)}
							className={tileWidth}
						>
							{tiles.income.present ? (
								<ViewButton onView={onViewIncome} />
							) : (
								<MutedText>Not Available</MutedText>
							)}
						</VerificationTile>
						<VerificationTile
							tone="green"
							icon={<CircleDollarSign />}
							title="Rent Verification"
							barTone={verificationBarTone(tiles.rent.status)}
							className={tileWidth}
						>
							{tiles.rent.present ? (
								<ViewButton onView={onViewRent} />
							) : (
								<MutedText>Not Available</MutedText>
							)}
						</VerificationTile>
						{showCourtTile ? (
							<VerificationTile
								tone="purple"
								icon={<FileText />}
								title="Court Records"
								barTone={court.barTone}
							>
								{court.url ? (
									<ViewButton onView={() => setCourtDialogOpen(true)} />
								) : (
									<MutedText>{court.text}</MutedText>
								)}
							</VerificationTile>
						) : null}
						{isFull ? (
							<VerificationTile
								tone="rose"
								icon={<ClipboardPlus />}
								title="Credit Report"
								barTone={creditAvailable ? 'green' : 'neutral'}
							>
								{creditAvailable && onViewCredit ? (
									<ViewButton onView={onViewCredit} />
								) : (
									<MutedText>Not Available</MutedText>
								)}
							</VerificationTile>
						) : null}
					</>
				) : null}
			</div>
			{!idOnly ? (
				<CourtRecordsDialog
					open={courtDialogOpen}
					onOpenChange={setCourtDialogOpen}
					records={courtRecords}
				/>
			) : null}
		</section>
	)
}
